import * as React from 'react';

import { ActiveDiagram } from 'src/containers/ProjectDiagram/types/diagram';
import { clearDiagramUndoHistory } from 'src/containers/ProjectDiagram/utils/clearDiagramUndoHistory';
import { isProjectReadOnlySelector } from 'src/selectors/isProjectReadOnly';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

/**
 * Locks the GoJS canvas for read-only collaborators by mirroring the
 * `collaboration` slice's per-project read-only flag onto
 * `diagram.isReadOnly`. This blocks user gestures (dragging, relinking,
 * keyboard edits) while leaving scroll/zoom/select and programmatic model
 * changes — like the remote merges in `useCollaborationDiagramSync` —
 * working. Toggles both ways at runtime so writer reassignment
 * promotes/demotes the canvas live.
 *
 * Also feeds the headless facade: `commitDiagramModel` gates on the same
 * `isReadOnly` flag, so mirroring it here keeps the list view's edits blocked
 * for read-only collaborators too.
 */
export const useDiagramReadOnly = (diagram: ActiveDiagram | undefined) => {
  const { projectId } = useRouterParams();
  const isReadOnly = useReduxSelector(isProjectReadOnlySelector(projectId));

  React.useEffect(() => {
    if (diagram) {
      // eslint-disable-next-line no-param-reassign
      diagram.isReadOnly = isReadOnly;
      if (isReadOnly && !diagram.headless) {
        // The toolbar's undo/redo call `undoManager.undo()` programmatically,
        // bypassing `isReadOnly` — wipe any pre-demotion history so an editor
        // demoted mid-session cannot mutate the locked canvas through it.
        // (Headless has no undo manager / canvas.)
        clearDiagramUndoHistory(diagram, projectId);
      }
    }
  }, [diagram, isReadOnly, projectId]);
};
